import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { fetchRequest, fetchRequestOffers, createOffer, acceptOffer, withdrawOffer, closeRequest } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Clock, Coins, Users, Check, X, ArrowLeft, ShieldCheck, Star } from "lucide-react";

export default function RequestDetailPage() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({ price: "", delivery_days: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    fetchRequest(id).then(setR).catch(() => setR(false));
    if (user) fetchRequestOffers(id).then(setOffers).catch(() => setOffers([]));
  };
  useEffect(load, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (r === false) return <div className="p-16 text-center">Request not found. <Link to="/requests" className="text-primary">Back to feed</Link></div>;
  if (!r) return <div className="p-16 text-center">Loading…</div>;

  const isBuyer = user?.user_id === r.buyer_id;
  const myOffer = offers.find((o) => o.seller_id === user?.user_id);
  const canPitch = user && !isBuyer && r.status === "open" && !myOffer;

  const submitOffer = async () => {
    const price = Number(form.price);
    const days = Number(form.delivery_days);
    if (!price || price <= 0) return toast.error("Enter a price");
    if (!days || days <= 0) return toast.error("Enter delivery days");
    if (form.note.trim().length < 10) return toast.error("Pitch must be at least 10 characters");
    setSaving(true);
    try {
      await createOffer(id, { price, delivery_days: days, note: form.note.trim() });
      toast.success("Offer sent");
      setForm({ price: "", delivery_days: "", note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const accept = async (o) => {
    if (!confirm(`Accept offer from ${o.seller_name} for $${o.price}? An order will be created and you'll be sent to payment.`)) return;
    try {
      const order = await acceptOffer(o.offer_id);
      toast.success("Offer accepted — complete payment to kick off");
      navigate(`/order/${order.order_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const withdraw = async (o) => {
    if (!confirm("Withdraw your offer?")) return;
    try { await withdrawOffer(o.offer_id); toast.success("Offer withdrawn"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const close = async () => {
    if (!confirm("Close this request? Sellers can no longer submit offers.")) return;
    try { await closeRequest(id); toast.success("Closed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-10">
      <Link to="/requests" className="mono text-xs text-muted-foreground flex items-center gap-1 hover:text-foreground mb-4"><ArrowLeft className="w-3 h-3"/> BACK TO FEED</Link>

      {/* Request header */}
      <div className="border border-foreground/15 p-6 bg-card mb-8">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="mono text-[10px] px-2 py-0.5 border border-foreground/30 uppercase">{r.category}</span>
          <span data-testid="req-status" className={`mono text-[10px] px-2 py-0.5 border uppercase ${r.status === "open" ? "border-primary text-primary" : "border-foreground/40 opacity-70"}`}>{r.status}</span>
          <span className="mono text-[10px] opacity-60">{new Date(r.created_at).toLocaleString()}</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-3" data-testid="req-title-display">{r.title}</h1>
        <p className="whitespace-pre-wrap text-foreground/90 mb-5">{r.description}</p>

        <div className="grid grid-cols-3 gap-4 border-t border-foreground/10 pt-4">
          <Stat icon={<Coins className="w-4 h-4 text-primary"/>} label="Budget" value={format(r.budget, { decimals: 0 })}/>
          <Stat icon={<Clock className="w-4 h-4"/>} label="Delivery" value={`${r.delivery_days} days`}/>
          <Stat icon={<Users className="w-4 h-4"/>} label="Offers" value={r.offers_count}/>
        </div>

        <div className="flex items-center justify-between mt-5 border-t border-foreground/10 pt-4">
          <div className="flex items-center gap-2">
            {r.buyer_picture ? <img src={r.buyer_picture} alt="" className="w-7 h-7 rounded-full object-cover"/> : <div className="w-7 h-7 rounded-full bg-foreground/10 flex items-center justify-center text-xs">{r.buyer_name?.[0]}</div>}
            <span className="text-sm text-muted-foreground">Posted by <b className="text-foreground">{r.buyer_name}</b></span>
          </div>
          {isBuyer && r.status === "open" && (
            <button data-testid="req-close-btn" onClick={close} className="mono text-xs border border-destructive text-destructive px-3 py-1.5 hover:bg-destructive hover:text-destructive-foreground">CLOSE REQUEST</button>
          )}
        </div>
      </div>

      {/* Pitch form for sellers */}
      {canPitch && (
        <div data-testid="pitch-form" className="border border-primary/40 bg-primary/5 p-6 mb-8">
          <div className="overline mb-1 text-primary">◆ SEND A CUSTOM OFFER</div>
          <h2 className="text-xl font-bold mb-4">Pitch your delivery, price and time.</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="overline block mb-2">◆ Price (USD)</label>
              <Input data-testid="pitch-price" type="number" min="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none border-foreground/20 mono"/>
            </div>
            <div>
              <label className="overline block mb-2">◆ Delivery (days)</label>
              <Input data-testid="pitch-days" type="number" min="1" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} className="rounded-none border-foreground/20 mono"/>
            </div>
          </div>
          <label className="overline block mb-2">◆ Message to buyer</label>
          <Textarea data-testid="pitch-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={4} placeholder="Introduce yourself, describe your approach, share relevant work…" className="rounded-none border-foreground/20"/>
          <div className="flex justify-end mt-3">
            <button data-testid="pitch-submit" onClick={submitOffer} disabled={saving} className="bg-primary text-primary-foreground px-6 py-2.5 mono text-xs hover:bg-primary/90 disabled:opacity-40">
              {saving ? "SENDING…" : "SEND OFFER"}
            </button>
          </div>
        </div>
      )}

      {!user && r.status === "open" && (
        <div className="border border-foreground/15 p-5 mb-8 text-center">
          <Link to="/login" className="mono text-xs bg-primary text-primary-foreground px-5 py-2">SIGN IN TO SEND AN OFFER</Link>
        </div>
      )}

      {/* Offers */}
      {(isBuyer || myOffer) && (
        <div>
          <div className="overline mb-4">◆ OFFERS ({offers.length})</div>
          {offers.length === 0 ? (
            <div className="border border-dashed border-foreground/20 p-8 text-center text-sm text-muted-foreground">No offers yet.</div>
          ) : (
            <div className="grid gap-4">
              {offers.map((o) => (
                <div key={o.offer_id} data-testid={`offer-${o.offer_id}`} className={`border ${o.status === "accepted" ? "border-primary" : "border-foreground/15"} bg-card p-5`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {o.seller_picture ? <img src={o.seller_picture} alt="" className="w-10 h-10 rounded-full object-cover"/> : <div className="w-10 h-10 rounded-full bg-foreground/10 flex items-center justify-center">{o.seller_name?.[0]}</div>}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Link to={`/seller/${o.seller_id}`} className="font-semibold hover:text-primary">{o.seller_name}</Link>
                          {o.seller_rating > 0 && (
                            <span className="flex items-center gap-0.5 text-xs">
                              <Star className="w-3 h-3 fill-primary text-primary"/> {o.seller_rating.toFixed(1)} <span className="opacity-60">({o.seller_rating_count})</span>
                            </span>
                          )}
                          {o.status === "accepted" && <span className="mono text-[10px] px-2 py-0.5 bg-primary text-primary-foreground uppercase">Accepted</span>}
                          {o.status === "rejected" && <span className="mono text-[10px] px-2 py-0.5 border border-foreground/30 opacity-70 uppercase">Rejected</span>}
                          {o.status === "withdrawn" && <span className="mono text-[10px] px-2 py-0.5 border border-foreground/30 opacity-70 uppercase">Withdrawn</span>}
                        </div>
                        <p className="text-sm text-foreground/90 whitespace-pre-wrap">{o.note}</p>
                      </div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="mono text-2xl font-bold">{format(o.price, { decimals: 0 })}</div>
                      <div className="mono text-xs text-muted-foreground mt-0.5"><Clock className="w-3 h-3 inline mr-1"/>{o.delivery_days}d</div>
                    </div>
                  </div>
                  {(isBuyer && r.status === "open" && o.status === "pending") && (
                    <div className="flex justify-end gap-2 border-t border-foreground/10 pt-3 mt-4">
                      <button data-testid={`offer-accept-${o.offer_id}`} onClick={() => accept(o)} className="mono text-xs bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/90 flex items-center gap-1.5"><Check className="w-3 h-3"/> ACCEPT & PAY</button>
                    </div>
                  )}
                  {(!isBuyer && o.seller_id === user?.user_id && o.status === "pending" && r.status === "open") && (
                    <div className="flex justify-end gap-2 border-t border-foreground/10 pt-3 mt-4">
                      <button data-testid={`offer-withdraw-${o.offer_id}`} onClick={() => withdraw(o)} className="mono text-xs border border-destructive text-destructive px-4 py-2 hover:bg-destructive hover:text-destructive-foreground flex items-center gap-1.5"><X className="w-3 h-3"/> WITHDRAW</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      {icon}
      <div>
        <div className="overline text-[10px] opacity-70">{label}</div>
        <div className="font-semibold mono">{value}</div>
      </div>
    </div>
  );
}
